import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { RefreshCw, RotateCcw, ShieldCheck } from 'lucide-react';
import { AdminLayout } from '../components/AdminLayout';
import {
  applyAiDecisionAction,
  getAiDecisions,
  type AiDecisionAction,
  type AiDecisionFilters,
  type AiDecisionListResponse,
  type AiDecisionType,
} from '../services/adminApi';
import {
  Banner,
  ButtonRow,
  Card,
  CardDescription,
  CardTitle,
  Field,
  Input,
  Label,
  PrimaryButton,
  RawJson,
  SecondaryButton,
  Select,
} from '../components/admin/AdminUI';
import { colors, spacing, typography, borderRadius, breakpoints } from '../styles/theme';

const FilterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(140px, 1fr));
  gap: ${spacing[3]};

  @media (max-width: ${breakpoints.lg}) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: ${breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const TableWrap = styled.div`
  overflow-x: auto;
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.lg};
`;

const Table = styled.table`
  width: 100%;
  min-width: 900px;
  border-collapse: collapse;
  font-size: ${typography.fontSizes.sm};

  th,
  td {
    padding: ${spacing[3]};
    text-align: left;
    vertical-align: top;
    border-bottom: 1px solid ${colors.border.light};
  }

  th {
    background: ${colors.background.secondary};
    color: ${colors.text.secondary};
    font-size: ${typography.fontSizes.xs};
    text-transform: uppercase;
    letter-spacing: 0;
  }
`;

const Status = styled.span`
  display: inline-flex;
  padding: 2px ${spacing[2]};
  border-radius: ${borderRadius.full};
  background: ${colors.background.tertiary};
  color: ${colors.text.secondary};
  font-size: ${typography.fontSizes.xs};
  font-weight: ${typography.fontWeights.semibold};
`;

const RowActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[2]};
  margin-top: ${spacing[2]};

  button {
    min-height: 36px;
    padding: ${spacing[1]} ${spacing[2]};
  }
`;

const Empty = styled.div`
  padding: ${spacing[8]};
  text-align: center;
  color: ${colors.text.secondary};
`;

const DECISION_TYPES: Array<{ value: AiDecisionType; label: string }> = [
  { value: 'source', label: 'Source relationships' },
  { value: 'relationship', label: 'Event relationships' },
  { value: 'event', label: 'Event classifications' },
  { value: 'logo', label: 'Logo candidates' },
  { value: 'audit', label: 'Audit history' },
];

function value(item: Record<string, any>, ...keys: string[]): any {
  for (const key of keys) if (item[key] !== undefined && item[key] !== null) return item[key];
  return null;
}

function subject(item: Record<string, any>, type: AiDecisionType): string {
  if (type === 'source') return `${value(item, 'EntityName') || 'Entity'} · ${value(item, 'NormalizedHost', 'CanonicalUrl') || 'Source'}`;
  if (type === 'relationship') return `${value(item, 'EventTitle') || 'Event'} · ${value(item, 'EntityName') || 'Entity'}`;
  if (type === 'event') return value(item, 'EventTitle') || `Event ${value(item, 'EventInfoId')}`;
  if (type === 'logo') return `${value(item, 'EntityName') || 'Entity'} · ${value(item, 'WebIqTitle', 'OriginalSourceUrl') || 'Candidate'}`;
  return `${value(item, 'DecisionType') || 'Audit'} · ${value(item, 'DecisionKey') || ''}`;
}

function status(item: Record<string, any>, type: AiDecisionType): string {
  if (type === 'source') return value(item, 'RelevanceStatus') || 'unscored';
  if (type === 'relationship') return value(item, 'DecisionStatus') || 'unscored';
  if (type === 'event') return value(item, 'EffectiveStatus') || 'RELEVANT';
  if (type === 'logo') return value(item, 'AssetStatus') || 'candidate';
  return value(item, 'DecisionType') || 'audit';
}

function score(item: Record<string, any>, type: AiDecisionType): string {
  const raw = type === 'source'
    ? value(item, 'RelevanceScore')
    : type === 'relationship'
      ? value(item, 'ConfidenceScore')
      : type === 'event'
        ? value(item, 'RelevanceConfidence')
        : type === 'logo'
          ? value(item, 'SelectionScore')
          : null;
  return raw == null ? 'Not scored' : `${Number(raw).toFixed(Number(raw) % 1 ? 1 : 0)}%`;
}

function timestamp(item: Record<string, any>): string {
  const raw = value(item, 'LastScoredAt', 'DateModified', 'RelevanceClassifiedAt', 'DateCreated');
  if (!raw) return 'Unknown';
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? String(raw) : date.toLocaleString();
}

export const AdminAiDecisionsPage: React.FC = () => {
  const [filters, setFilters] = useState<AiDecisionFilters>({
    decisionType: 'source',
    page: 1,
    limit: 25,
  });
  const [result, setResult] = useState<AiDecisionListResponse | null>(null);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setResult(await getAiDecisions(filters));
    } catch (loadError) {
      setError((loadError as Error).message);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { void load(); }, [load]);

  const mutate = useCallback(async (action: AiDecisionAction, needsReason = true) => {
    if (needsReason && !reason.trim()) {
      setError('Enter a reason before applying an audited correction.');
      return;
    }
    setActionLoading(true);
    setError(null);
    setNotice(null);
    try {
      const response = await applyAiDecisionAction({
        ...action,
        reason: needsReason ? reason.trim() : action.reason,
      });
      setNotice(response.status === 'queued' ? `Queued job ${response.jobId}` : 'Decision updated and audited.');
      await load();
    } catch (actionError) {
      setError((actionError as Error).message);
    } finally {
      setActionLoading(false);
    }
  }, [load, reason]);

  const rows = result?.items || [];
  const pageCount = useMemo(() => Math.max(1, Math.ceil((result?.total || 0) / (result?.limit || 25))), [result]);

  const rowActions = (item: Record<string, any>) => {
    const type = filters.decisionType;
    const entityId = Number(value(item, 'EntityId')) || undefined;
    const eventInfoId = Number(value(item, 'EventInfoId')) || undefined;
    const eventSourceId = Number(value(item, 'EventSourceId')) || undefined;
    const relationshipType = value(item, 'RelationshipType') || undefined;
    return (
      <RowActions>
        {type === 'event' && (
          <>
            <SecondaryButton disabled={actionLoading} onClick={() => mutate({
              action: 'set_event_relevance_override', eventInfoId, forcedStatus: 'RELEVANT',
            })}>Force relevant</SecondaryButton>
            <SecondaryButton disabled={actionLoading} onClick={() => mutate({
              action: 'set_event_relevance_override', eventInfoId, forcedStatus: 'EARNINGS_CALL',
            })}>Force earnings call</SecondaryButton>
            <SecondaryButton disabled={actionLoading} onClick={() => mutate({
              action: 'clear_event_relevance_override', eventInfoId,
            })}>Clear override</SecondaryButton>
          </>
        )}
        {type === 'source' && (
          <>
            <SecondaryButton disabled={actionLoading} onClick={() => mutate({
              action: 'correct_source_relationship', entityId, eventSourceId, relevanceStatus: 'confirmed',
            })}>Confirm</SecondaryButton>
            <SecondaryButton disabled={actionLoading} onClick={() => mutate({
              action: 'correct_source_relationship', entityId, eventSourceId, relevanceStatus: 'unlikely',
            })}>Mark unlikely</SecondaryButton>
            {value(item, 'DecisionMethod').toLowerCase() === 'manual' && (
              <SecondaryButton disabled={actionLoading} onClick={() => mutate({
                action: 'reset_source_relationship_correction', entityId, eventSourceId,
              })}><RotateCcw size={14} /> Reset correction</SecondaryButton>
            )}
          </>
        )}
        {type === 'relationship' && (
          <>
            <SecondaryButton disabled={actionLoading} onClick={() => mutate({
              action: 'correct_relationship', entityId, eventInfoId, relationshipType, decisionStatus: 'confirmed',
            })}>Confirm</SecondaryButton>
            <SecondaryButton disabled={actionLoading} onClick={() => mutate({
              action: 'correct_relationship', entityId, eventInfoId, relationshipType, decisionStatus: 'unlikely',
            })}>Mark unlikely</SecondaryButton>
            {value(item, 'DecisionMethod').toLowerCase() === 'manual' && (
              <SecondaryButton disabled={actionLoading} onClick={() => mutate({
                action: 'reset_relationship_correction', entityId, eventInfoId, relationshipType,
              })}><RotateCcw size={14} /> Reset correction</SecondaryButton>
            )}
          </>
        )}
        {type === 'logo' && (
          <>
            <SecondaryButton disabled={actionLoading || !value(item, 'Sha256Hash')} onClick={() => mutate({
              action: 'select_logo', entityId, assetId: Number(value(item, 'AssetId')),
            })}>Select logo</SecondaryButton>
            {Boolean(value(item, 'IsSelected')) && (
              <SecondaryButton disabled={actionLoading} onClick={() => mutate({
                action: 'remove_logo', entityId,
              })}>Remove logo</SecondaryButton>
            )}
            {value(item, 'LogoSelectionMethod').toLowerCase() === 'manual' && (
              <SecondaryButton disabled={actionLoading} onClick={() => mutate({
                action: 'reset_logo_selection', entityId,
              })}><RotateCcw size={14} /> Allow automatic selection</SecondaryButton>
            )}
          </>
        )}
        {type !== 'audit' && (
          <SecondaryButton disabled={actionLoading} onClick={() => mutate({
            action: 'rescore', decisionType: type, entityId, eventInfoId,
            eventSourceId, relationshipType,
          }, false)}>
            <RotateCcw size={14} /> Rescore
          </SecondaryButton>
        )}
      </RowActions>
    );
  };

  return (
    <AdminLayout
      currentPage="aiDecisions"
      pageTitle="AI Decisions"
      pageDescription="Inspect automated relevance, relationship, classification, logo, and freshness decisions. Routine processing does not wait for this page."
    >
      {error && <Banner tone="error">{error}</Banner>}
      {notice && <Banner tone="success">{notice}</Banner>}

      <Card>
        <CardTitle>Filters</CardTitle>
        <CardDescription>Filter current snapshots or append-only audit history before inspecting evidence and prior values.</CardDescription>
        <FilterGrid>
          <Field>
            <Label htmlFor="decision-type">Decision type</Label>
            <Select id="decision-type" value={filters.decisionType} onChange={event => setFilters({
              decisionType: event.target.value as AiDecisionType, page: 1, limit: filters.limit,
            })}>
              {DECISION_TYPES.map(type => <option key={type.value} value={type.value}>{type.label}</option>)}
            </Select>
          </Field>
          <Field><Label htmlFor="entity-id">Entity ID</Label><Input id="entity-id" value={filters.entityId || ''} onChange={event => setFilters({ ...filters, entityId: event.target.value, page: 1 })} /></Field>
          <Field><Label htmlFor="event-id">Event ID</Label><Input id="event-id" value={filters.eventId || ''} onChange={event => setFilters({ ...filters, eventId: event.target.value, page: 1 })} /></Field>
          <Field><Label htmlFor="source-id">Source ID</Label><Input id="source-id" value={filters.sourceId || ''} onChange={event => setFilters({ ...filters, sourceId: event.target.value, page: 1 })} /></Field>
          <Field><Label htmlFor="status">Status/classification</Label><Input id="status" value={filters.status || ''} onChange={event => setFilters({ ...filters, status: event.target.value, page: 1 })} /></Field>
          <Field><Label htmlFor="role">Role</Label><Input id="role" value={filters.role || ''} onChange={event => setFilters({ ...filters, role: event.target.value, page: 1 })} /></Field>
          <Field><Label htmlFor="min-confidence">Minimum confidence</Label><Input id="min-confidence" type="number" min="0" max="100" value={filters.minConfidence || ''} onChange={event => setFilters({ ...filters, minConfidence: event.target.value, page: 1 })} /></Field>
          <Field><Label htmlFor="max-confidence">Maximum confidence</Label><Input id="max-confidence" type="number" min="0" max="100" value={filters.maxConfidence || ''} onChange={event => setFilters({ ...filters, maxConfidence: event.target.value, page: 1 })} /></Field>
          <Field><Label htmlFor="date-from">From</Label><Input id="date-from" type="date" value={filters.dateFrom || ''} onChange={event => setFilters({ ...filters, dateFrom: event.target.value, page: 1 })} /></Field>
          <Field><Label htmlFor="date-to">To</Label><Input id="date-to" type="date" value={filters.dateTo || ''} onChange={event => setFilters({ ...filters, dateTo: event.target.value, page: 1 })} /></Field>
        </FilterGrid>
        <Field style={{ marginTop: spacing[4] }}>
          <Label htmlFor="audit-reason">Correction reason</Label>
          <Input id="audit-reason" value={reason} onChange={event => setReason(event.target.value)} placeholder="Required for manual corrections and overrides" />
        </Field>
        <ButtonRow>
          <PrimaryButton disabled={loading} onClick={() => void load()}>
            <RefreshCw size={16} /> {loading ? 'Loading...' : 'Refresh'}
          </PrimaryButton>
        </ButtonRow>
      </Card>

      <Card>
        <CardTitle>{DECISION_TYPES.find(type => type.value === filters.decisionType)?.label}</CardTitle>
        <CardDescription>{`${result?.total || 0} records · page ${result?.page || 1} of ${pageCount}`}</CardDescription>
        {rows.length === 0 ? <Empty>{loading ? 'Loading decisions...' : 'No decisions match these filters.'}</Empty> : (
          <TableWrap>
            <Table>
              <thead><tr><th>Subject</th><th>Status</th><th>Confidence</th><th>Method / model</th><th>Updated</th><th>Inspect / correct</th></tr></thead>
              <tbody>
                {rows.map((item, index) => (
                  <tr key={`${filters.decisionType}-${value(item, 'Id', 'AssetId', 'EventInfoId') || index}`}>
                    <td><strong>{subject(item, filters.decisionType)}</strong></td>
                    <td><Status>{status(item, filters.decisionType)}</Status></td>
                    <td>{score(item, filters.decisionType)}</td>
                    <td>{value(item, 'DecisionMethod', 'RelevanceDecisionMethod', 'ScoringModel', 'RelevanceModel') || 'Unknown'}</td>
                    <td>{timestamp(item)}</td>
                    <td>
                      <RawJson data={item} label="Evidence, contradictions, versions, and prior values" />
                      {rowActions(item)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        )}
        <ButtonRow>
          <SecondaryButton disabled={(result?.page || 1) <= 1} onClick={() => setFilters({ ...filters, page: Math.max(1, (result?.page || 1) - 1) })}>Previous</SecondaryButton>
          <SecondaryButton disabled={(result?.page || 1) >= pageCount} onClick={() => setFilters({ ...filters, page: (result?.page || 1) + 1 })}>Next</SecondaryButton>
        </ButtonRow>
      </Card>

      <Card>
        <CardTitle><ShieldCheck size={18} /> Automated processing remains authoritative</CardTitle>
        <CardDescription>Unreviewed items continue to resolve automatically to confirmed, unlikely, relevant, earnings call, or error. Manual overrides are audited and remain until explicitly cleared.</CardDescription>
      </Card>
    </AdminLayout>
  );
};

export default AdminAiDecisionsPage;
